import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ChatTurn, TemplateDef } from "@juro/shared";
import { aiApi, getDocTypeLabel, templatesApi, type DocType } from "../api.js";
import { createSkeletonTemplate } from "../lib/createBlankTemplate.js";
import { fileToBackgroundImage } from "../lib/fileToBackgroundImage.js";
import { extractPdfTextItems, type PdfPageInfo } from "../lib/extractPdfTextItems.js";
import { useEditorStore } from "../stores/editorStore.js";
import { useAIStore } from "../stores/aiStore.js";
import { Modal } from "../components/Modal.js";
import { Button } from "../components/ui/Button.js";
import { toast } from "../components/Toast.js";
import { useScreenView, trackFeatureAbandoned, setTemplateDraftOrigin } from "../lib/analytics.js";

/** 源泉所得税を既定でオンにする書類種別（business-logic.md §2） */
const WITHHOLDING_DOC_TYPES = new Set(["judicial_invoice", "surveyor_invoice", "guarantee_invoice", "estimate"]);

const methods = [
  {
    icon: "AI",
    title: "AIに作ってもらう",
    desc: "どんな書類か説明するだけで、AIがグリッドと数式を含むテンプレート案を作ります。",
    action: "ai_chat",
    primary: true,
  },
  {
    icon: "PDF",
    title: "既存PDFから取り込む",
    desc: "現在使っているPDFや画像からレイアウトを読み取り、V2テンプレートへ変換します。",
    action: "from_pdf",
  },
  {
    icon: "LIB",
    title: "ライブラリから選ぶ",
    desc: "司法書士・行政書士・調査士の請求書、見積書のサンプルをプレビューで確認してコピーします。",
    action: "library",
  },
  {
    icon: "BLK",
    title: "空のテンプレートから",
    desc: "標準の骨格（ヘッダー・明細・合計欄）または白紙から、Excel風グリッドエディタで自由に設計します。",
    action: "blank",
  },
] as const;

/** 「空のテンプレートから」の開始オプション選択モーダル */
function BlankStartModal({
  docType,
  onStart,
  onClose,
}: {
  docType: DocType;
  onStart: (opts: { orientation: "portrait" | "landscape"; withSkeleton: boolean }) => void;
  onClose: () => void;
}) {
  const [orientation, setOrientation] = useState<"portrait" | "landscape">("portrait");
  const [withSkeleton, setWithSkeleton] = useState(true);

  const radioRow = (checked: boolean) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "12px 14px",
    border: `1px solid ${checked ? "#5eead4" : "#e2e8f0"}`,
    background: checked ? "#f0fdfa" : "#fff",
    borderRadius: 8,
    cursor: "pointer",
  } as const);

  return (
    <Modal title={`${getDocTypeLabel(docType)}テンプレートを新規作成`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>構成</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={radioRow(withSkeleton)}>
              <input
                type="radio"
                name="skeleton"
                checked={withSkeleton}
                onChange={() => setWithSkeleton(true)}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>標準の骨格から（推奨）</span>
                <span style={{ display: "block", fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  タイトル・宛名・発行者情報・明細表・合計欄が配置済み。不要な部分を消して調整できます。
                </span>
              </span>
            </label>
            <label style={radioRow(!withSkeleton)}>
              <input
                type="radio"
                name="skeleton"
                checked={!withSkeleton}
                onChange={() => setWithSkeleton(false)}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>白紙から</span>
                <span style={{ display: "block", fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  タイトルのみ。ブロックを1つずつ追加して組み立てます。
                </span>
              </span>
            </label>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>用紙の向き（A4）</div>
          <div style={{ display: "flex", gap: 8 }}>
            {([["portrait", "縦"], ["landscape", "横"]] as const).map(([value, label]) => (
              <label key={value} style={{ ...radioRow(orientation === value), flex: 1, alignItems: "center" }}>
                <input
                  type="radio"
                  name="orientation"
                  checked={orientation === value}
                  onChange={() => setOrientation(value)}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>キャンセル</Button>
          <Button onClick={() => onStart({ orientation, withSkeleton })}>作成する</Button>
        </div>
      </div>
    </Modal>
  );
}

/** 「AIに作ってもらう」の構造化ヒアリング＋対話ヒアリング（最大2往復）モーダル */
function AIStartModal({
  docType,
  onGenerated,
  onClose,
}: {
  docType: DocType;
  onGenerated: (template: TemplateDef, summary: string, fallback: boolean) => void;
  onClose: () => void;
}) {
  const [instruction, setInstruction] = useState("");
  const [withLineItems, setWithLineItems] = useState(true);
  const [withWithholding, setWithWithholding] = useState(WITHHOLDING_DOC_TYPES.has(docType));
  const [withBankInfo, setWithBankInfo] = useState(true);
  const [orientation, setOrientation] = useState<"portrait" | "landscape">("portrait");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 対話ヒアリング（clarify）の往復履歴。空のままなら通常どおり一発生成
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [answer, setAnswer] = useState("");

  const options = {
    with_line_items: withLineItems,
    with_withholding: withWithholding,
    with_bank_info: withBankInfo,
    orientation,
  };

  /**
   * `history` はこの発言より前の往復履歴（サーバーへ送る conversation。この発言自体は含めない。
   * AIPanel（編集側）の積み方と揃え、直近の発言は instruction 側でのみ送る）。
   * 成功時は true、失敗時は false を返す（呼び出し側で入力状態の復元に使う）。
   */
  async function submit(nextInstruction: string, history: ChatTurn[]): Promise<boolean> {
    setError(null);
    setGenerating(true);
    try {
      const res = await aiApi.templateGenerate({
        doc_type: docType,
        instruction: nextInstruction,
        options,
        conversation: history,
      });
      if (res.type === "clarify") {
        // 表示・次回送信用の履歴には、今回の発言（ユーザー）と質問（アシスタント）の両方を積む
        setTurns([...history, { role: "user", content: nextInstruction }, { role: "assistant", content: res.question }]);
        setPendingQuestion(res.question);
        setGenerating(false);
      } else {
        onGenerated(res.template as unknown as TemplateDef, res.summary, res.fallback === true);
      }
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "生成に失敗しました");
      setGenerating(false);
      return false;
    }
  }

  function generate() {
    if (!instruction.trim()) {
      setError("書類の説明を入力してください");
      return;
    }
    // 1ターン目: conversation は空。最初の説明は clarify を受け取った時点で turns の先頭に積む
    void submit(instruction.trim(), []);
  }

  function reply() {
    if (!answer.trim()) {
      setError("回答を入力してください");
      return;
    }
    const trimmed = answer.trim();
    const questionBackup = pendingQuestion;
    // 楽観的にクリアするが、送信失敗時は復元する（M-2: 回答欄が消えたまま操作不能になるのを防ぐ）
    setPendingQuestion(null);
    setAnswer("");
    void (async () => {
      const ok = await submit(trimmed, turns);
      if (!ok) {
        setPendingQuestion(questionBackup);
        setAnswer(trimmed);
      }
    })();
  }

  const checkRow = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#0f172a",
    cursor: "pointer",
  } as const;

  const inHearing = turns.length > 0;

  return (
    <Modal title={`AIで${getDocTypeLabel(docType)}テンプレートを作る`} onClose={onClose} width={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {!inHearing && (
          <>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 6 }}>
                どんな書類ですか？
              </div>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder={"例: 相続登記の請求書。明細は「登記申請」「戸籍収集」「相談料」の3区分で、実費欄には登録免許税を記載する。"}
                rows={5}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  padding: 10,
                  fontSize: 13,
                  fontFamily: "inherit",
                  lineHeight: 1.7,
                  resize: "vertical",
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>含める要素</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={checkRow}>
                  <input type="checkbox" checked={withLineItems} onChange={(e) => setWithLineItems(e.target.checked)} />
                  明細表（日付・内容・金額の一覧）
                </label>
                <label style={checkRow}>
                  <input type="checkbox" checked={withWithholding} onChange={(e) => setWithWithholding(e.target.checked)} />
                  源泉所得税の行
                </label>
                <label style={checkRow}>
                  <input type="checkbox" checked={withBankInfo} onChange={(e) => setWithBankInfo(e.target.checked)} />
                  振込先・備考欄
                </label>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>用紙の向き（A4）</div>
              <div style={{ display: "flex", gap: 12 }}>
                {([["portrait", "縦"], ["landscape", "横"]] as const).map(([value, label]) => (
                  <label key={value} style={checkRow}>
                    <input type="radio" name="ai_orientation" checked={orientation === value} onChange={() => setOrientation(value)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {inHearing && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
              もう少し教えてください
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 260, overflowY: "auto", padding: "4px 2px" }}>
              {turns.map((turn, idx) => (
                <div
                  key={idx}
                  style={{
                    alignSelf: turn.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "88%",
                    background: turn.role === "user" ? "#0f766e" : "#f8fafc",
                    color: turn.role === "user" ? "#fff" : "#0f172a",
                    borderRadius: 10,
                    padding: "9px 11px",
                    fontSize: 13,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {turn.content}
                </div>
              ))}
            </div>
            {pendingQuestion && (
              <div style={{ marginTop: 12 }}>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="回答を入力してください"
                  rows={2}
                  disabled={generating}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 13,
                    fontFamily: "inherit",
                    lineHeight: 1.6,
                    resize: "vertical",
                  }}
                />
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={generating}>キャンセル</Button>
          {inHearing ? (
            <Button onClick={reply} disabled={generating}>
              {generating ? "生成中...（10秒ほどかかります）" : "回答する"}
            </Button>
          ) : (
            <Button onClick={generate} disabled={generating}>
              {generating ? "生成中...（10秒ほどかかります）" : "AIに生成してもらう"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** PdfImportModal の取込結果。AI認識の成否で開始状態が変わる */
export type PdfImportResult =
  | { kind: "manual"; assetId: string; withSkeleton: boolean }
  | { kind: "ai_success"; assetId: string; template: TemplateDef; summary: string }
  | { kind: "ai_fallback"; assetId: string; summary: string };

/** 画像（PNG/JPEG）取込時のページ寸法既定値（mm寸法が取得できないため A4縦を仮定） */
const DEFAULT_IMAGE_PAGE: PdfPageInfo = {
  width_mm: 210,
  height_mm: 297,
  orientation: "portrait",
  text_items: [],
};

/** 「既存PDFから取り込む」モーダル: 下敷き画像をアップロードし、AIでレイアウトを自動認識する（OFF時は従来のなぞり方式） */
function PdfImportModal({
  docType,
  onImported,
  onClose,
}: {
  docType: DocType;
  onImported: (result: PdfImportResult) => void;
  onClose: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [aiAssist, setAiAssist] = useState(true);
  const [withSkeleton, setWithSkeleton] = useState(true);
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function importFile() {
    if (!file) {
      setError("ファイルを選択してください");
      return;
    }
    setError(null);
    setImporting(true);
    try {
      const image = await fileToBackgroundImage(file);
      const { asset_id } = await templatesApi.uploadAsset(image.base64, image.contentType);

      if (!aiAssist) {
        onImported({ kind: "manual", assetId: asset_id, withSkeleton });
        return;
      }

      setImporting(false);
      setAnalyzing(true);
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      const pageInfo = isPdf ? await extractPdfTextItems(file) : DEFAULT_IMAGE_PAGE;

      const res = await aiApi.templateFromImport({
        doc_type: docType,
        asset_id,
        page: { width_mm: pageInfo.width_mm, height_mm: pageInfo.height_mm, orientation: pageInfo.orientation },
        text_items: pageInfo.text_items,
      });

      if (res.template) {
        onImported({ kind: "ai_success", assetId: asset_id, template: res.template as unknown as TemplateDef, summary: res.summary });
      } else {
        onImported({ kind: "ai_fallback", assetId: asset_id, summary: res.summary });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "取り込みに失敗しました");
      setImporting(false);
      setAnalyzing(false);
    }
  }

  const busy = importing || analyzing;

  return (
    <Modal title="既存PDFから取り込む" onClose={onClose} width={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>
          現在使っている書類（PDF・PNG・JPEG）の1ページ目を「下敷き」としてエディタに薄く表示します。
          下敷きに合わせてブロックを配置してください。下敷きは発行するPDFには含まれません。
        </p>

        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 6 }}>ファイル</div>
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            style={{ fontSize: 13 }}
          />
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#0f172a", cursor: "pointer" }}>
          <input type="checkbox" checked={aiAssist} onChange={(e) => setAiAssist(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            <span style={{ display: "block", fontWeight: 700 }}>AIでレイアウトを自動認識する（推奨）</span>
            <span style={{ display: "block", fontSize: 12, color: "#64748b", marginTop: 2 }}>
              読み取った骨組みを下敷きに重ねて表示します。ズレは後から調整できます。
            </span>
          </span>
        </label>

        {!aiAssist && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#0f172a", cursor: "pointer" }}>
            <input type="checkbox" checked={withSkeleton} onChange={(e) => setWithSkeleton(e.target.checked)} />
            標準の骨格（ヘッダー・明細・合計欄）も併せて配置する
          </label>
        )}

        {error && (
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={busy}>キャンセル</Button>
          <Button onClick={importFile} disabled={busy || !file}>
            {analyzing ? "レイアウトを解析中...（10〜20秒）" : importing ? "取り込み中..." : "取り込んで開始"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function TemplateNewPage() {
  useScreenView("template_new");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loadDraft = useEditorStore((state) => state.loadDraft);
  const openAI = useAIStore((state) => state.openPanel);
  const closeAI = useAIStore((state) => state.closePanel);
  const clearAIMessages = useAIStore((state) => state.clearMessages);
  const addAIMessage = useAIStore((state) => state.addMessage);
  const docType = (searchParams.get("type") ?? "judicial_invoice") as DocType;
  const [blankModalOpen, setBlankModalOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  function startDraft(opts: { orientation: "portrait" | "landscape"; withSkeleton: boolean }) {
    setTemplateDraftOrigin("blank");
    loadDraft(
      null,
      createSkeletonTemplate({
        name: `${getDocTypeLabel(docType)}テンプレート`,
        docType,
        orientation: opts.orientation,
        withSkeleton: opts.withSkeleton,
      }),
    );
    navigate(`/settings/templates/draft?type=${docType}`);
  }

  function handleAction(action: string) {
    if (action === "blank") {
      closeAI();
      setBlankModalOpen(true);
    }
    if (action === "ai_chat") {
      setAiModalOpen(true);
    }
    if (action === "library") {
      closeAI();
      navigate(`/settings/templates/library?type=${docType}`);
    }
    if (action === "from_pdf") {
      closeAI();
      setPdfModalOpen(true);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 980 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{getDocTypeLabel(docType)}</div>
        <h1 style={{ margin: 0, fontSize: 24, color: "#0f172a" }}>どうやって作りますか？</h1>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(260px, 1fr))", gap: 16 }}>
        {methods.map((method) => (
          <button
            key={method.action}
            onClick={() => handleAction(method.action)}
            style={{
              textAlign: "left",
              background: method.action === "ai_chat" ? "#f0fdfa" : "#fff",
              border: `1px solid ${method.action === "ai_chat" ? "#5eead4" : "#e2e8f0"}`,
              borderRadius: 8,
              padding: 20,
              cursor: "pointer",
              fontFamily: "inherit",
              minHeight: 148,
              boxShadow: method.action === "ai_chat" ? "0 10px 22px rgba(15,118,110,0.08)" : "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span style={{ width: 42, height: 42, display: "grid", placeItems: "center", borderRadius: 8, background: method.action === "ai_chat" ? "#0f766e" : "#f8fafc", color: method.action === "ai_chat" ? "#fff" : "#475569", fontSize: 12, fontWeight: 900 }}>
                {method.icon}
              </span>
              <div style={{ fontSize: 18, color: "#0f172a", fontWeight: 900 }}>{method.title}</div>
            </div>
            <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>{method.desc}</div>
          </button>
        ))}
      </div>

      {blankModalOpen && (
        <BlankStartModal
          docType={docType}
          onStart={startDraft}
          onClose={() => setBlankModalOpen(false)}
        />
      )}

      {pdfModalOpen && (
        <PdfImportModal
          docType={docType}
          onImported={(result) => {
            setTemplateDraftOrigin("pdf_import");

            if (result.kind === "ai_success") {
              loadDraft(null, {
                ...result.template,
                name: result.template.name ?? `${getDocTypeLabel(docType)}テンプレート`,
                background: { asset_id: result.assetId, opacity: 0.35, include_in_pdf: false },
              });
              toast(result.summary.split("\n")[0] ?? result.summary);
              navigate(`/settings/templates/draft?type=${docType}`);
              return;
            }

            // ai_fallback: 自動認識できなかった場合は「下敷きのみ」で開始（標準骨格は付けない。
            // 元PDFと無関係な骨格を重ねると下敷きと二重にズレて混乱するため。設計書 §4.4）
            // manual: AI未使用時の従来動作（withSkeleton オプションを尊重）
            const base = createSkeletonTemplate({
              name: `${getDocTypeLabel(docType)}テンプレート`,
              docType,
              withSkeleton: result.kind === "manual" ? result.withSkeleton : false,
            });
            loadDraft(null, {
              ...base,
              background: { asset_id: result.assetId, opacity: 0.35, include_in_pdf: false },
            });

            if (result.kind === "ai_fallback") {
              toast("自動認識できませんでした。下敷きに合わせて配置してください", "error");
            } else {
              toast("下敷きを取り込みました。プレビューに合わせてブロックを配置してください");
            }
            navigate(`/settings/templates/draft?type=${docType}`);
          }}
          onClose={() => {
            trackFeatureAbandoned("pdf_import");
            setPdfModalOpen(false);
          }}
        />
      )}

      {aiModalOpen && (
        <AIStartModal
          docType={docType}
          onGenerated={(template, summary, fallback) => {
            setTemplateDraftOrigin("ai");
            loadDraft(null, {
              ...template,
              name: template.name ?? `${getDocTypeLabel(docType)}テンプレート`,
            });
            clearAIMessages();
            addAIMessage({
              role: "assistant",
              content: `${summary}\nプレビューを確認して、修正したい点をチャットで指示してください（例:「振込先の下に備考欄を追加して」）。`,
            });
            toast(summary.split("\n")[0] ?? summary, fallback ? "error" : "success");
            navigate(`/settings/templates/draft?type=${docType}`);
            openAI();
          }}
          onClose={() => {
            trackFeatureAbandoned("ai_generate");
            setAiModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
